import type { ComposeInput } from '@proofroom/schema';
import { composeHere, type ComposeResult } from './adapter';
import type { ComposeRequest, ComposeResponse } from './worker';

export interface Composer {
  compose(input: ComposeInput): Promise<ComposeResult>;
  dispose(): void;
}

/** Runs composition off the main thread when Workers exist (always in browsers), inline otherwise. */
export function createComposer(): Composer {
  if (typeof Worker === 'undefined') {
    return { compose: (input) => composeHere(input), dispose: () => {} };
  }
  let worker: Worker | undefined;
  let seq = 0;
  const pending = new Map<number, { resolve: (r: ComposeResult) => void; reject: (e: Error) => void }>();
  const spawn = (): Worker => {
    const w = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'proofroom-compose' });
    w.onmessage = (ev: MessageEvent<ComposeResponse>) => {
      const p = pending.get(ev.data.id);
      if (!p) return;
      pending.delete(ev.data.id);
      if ('error' in ev.data) p.reject(new Error(ev.data.error)); else p.resolve(ev.data.result);
    };
    w.onerror = (ev) => {
      console.warn('[compose worker] crashed, composing inline', ev.message);
      for (const p of pending.values()) p.reject(new Error(ev.message));
      pending.clear();
      worker = undefined;
    };
    return w;
  };
  return {
    compose(input) {
      const id = ++seq;
      try {
        worker ??= spawn();
        const w = worker;
        return new Promise<ComposeResult>((resolve, reject) => {
          pending.set(id, { resolve, reject });
          w.postMessage({ id, input } satisfies ComposeRequest);
        }).catch(() => composeHere(input));
      } catch {
        return composeHere(input);
      }
    },
    dispose() { worker?.terminate(); worker = undefined; pending.clear(); },
  };
}
